import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, UserRole } from "@/contexts/AuthContext";
import UserMenu from "@/components/feature/UserMenu";
import Logo from "@/components/feature/Logo";
import { getUnreadCount } from "@/lib/notifications";

/**
 * The bell goes to the inbox that belongs to the signed-in user, decided by role.
 *
 * It used to branch on the current URL, which meant a provider anywhere outside
 * /proveedor/* — the public site, a tour page, their own /perfil pages — was sent to the
 * client inbox instead of theirs. Role is the thing that actually determines whose inbox
 * this is, so it is what the destination keys off.
 */
function notificationsHrefFor(role: UserRole): string {
  if (role === UserRole.Admin) return "/admin/notificaciones";
  if (role === UserRole.Provider) return "/proveedor/notificaciones";
  return "/perfil/notificaciones";
}

export default function DashboardTopbar() {
  const { t, i18n } = useTranslation("home");
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) { setUnread(0); return; }
    let cancelled = false;
    getUnreadCount()
      .then((r) => { if (!cancelled) setUnread(r.count); })
      .catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, [user, location.pathname]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggleLang = (lang: string) => {
    i18n.changeLanguage(lang);
    setLangOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 h-14 md:h-20 bg-white/95 backdrop-blur-sm shadow-sm">
      <div className="w-full h-full px-4 md:px-8 lg:px-12 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0 text-charcoal">
          <Logo size={32} withWordmark wordmarkClassName="font-display text-lg md:text-xl font-bold tracking-tight" />
        </Link>

        <div className="flex items-center gap-2 md:gap-3">
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-sm font-medium text-charcoal hover:bg-gray-50 transition-colors"
              aria-label={t("header.language")}
            >
              <span>{i18n.language === "es" ? "ES" : "EN"}</span>
              <i className={`ri-arrow-down-s-line ${langOpen ? "rotate-180" : ""} transition-transform`} />
            </button>
            {langOpen && (
              <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-100 py-1 min-w-[110px] z-50">
                <button
                  onClick={() => toggleLang("es")}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                    i18n.language === "es" ? "text-ocean font-semibold" : "text-charcoal"
                  }`}
                >
                  Español
                </button>
                <button
                  onClick={() => toggleLang("en")}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                    i18n.language === "en" ? "text-ocean font-semibold" : "text-charcoal"
                  }`}
                >
                  English
                </button>
              </div>
            )}
          </div>

          {user && (
            <Link
              to={notificationsHrefFor(user.role)}
              className="hidden sm:flex relative w-9 h-9 items-center justify-center rounded-full text-charcoal hover:bg-gray-50 transition-colors"
              aria-label={t("nav.bookings")}
              title={t("nav.bookings")}
            >
              <i className="ri-notification-3-line text-lg" />
              {unread > 0 && (
                <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-coral text-white text-[10px] font-bold leading-none">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </Link>
          )}

          {user ? (
            <UserMenu user={user} onLogout={handleLogout} />
          ) : (
            <>
              <Link
                to="/login"
                className="hidden sm:inline-flex text-sm font-medium text-charcoal hover:text-turquoise transition-colors px-3 py-1.5"
              >
                {t("nav.login")}
              </Link>
              <Link
                to="/registro"
                className="bg-ocean hover:bg-ocean/90 text-white text-sm font-medium px-4 py-2 rounded-full transition-colors whitespace-nowrap"
              >
                {t("nav.register")}
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
